import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, onValue, get, push, set, update, runTransaction, serverTimestamp } from 'firebase/database';
import './Borrow.css';
import { fetchContact, recordConnection } from '../contacts';

const CAR_MAKES = ['Toyota','Honda','Ford','Chevrolet','BMW','Mercedes','Audi','Tesla','Nissan','Mazda','Subaru','Volkswagen','Jeep','Dodge','Other'];
const TRANSMISSION_TYPES = ['Automatic', 'Manual'];
const FUEL_TYPES = ['Gas', 'Electric', 'Hybrid', 'Diesel'];
const SEAT_OPTIONS = ['2', '4', '5', '6', '7', '8+'];

function Borrow() {
  const [cars, setCars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterAvailable, setFilterAvailable] = useState(true);

  // Booking modal
  const [selectedCar, setSelectedCar] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [bookingMessage, setBookingMessage] = useState('');
  const [isBooking, setIsBooking] = useState(false);

  // Post car modal
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({
    make: '', model: '', year: '', color: '', licensePlate: '',
    pricePerDay: '', location: '', description: '',
    mileage: '', transmission: 'Automatic', fuelType: 'Gas', seats: '5',
    availableOn: '',
  });
  const [isPosting, setIsPosting] = useState(false);

  const auth = getAuth();
  const db = getDatabase();

  useEffect(() => {
    const isExpired = (dateStr) => {
      if (!dateStr) return false;
      return new Date(dateStr).getTime() + 24 * 60 * 60 * 1000 < Date.now();
    };

    const carsRef = ref(db, 'cars');
    const unsubscribe = onValue(carsRef, async (snapshot) => {
      // Fetch rentalBookings once to handle old cars without rentalEndDate
      // (Only rentals you're part of may be readable — treat a denied read as none.)
      const rentSnap = await get(ref(db, 'rentalBookings')).catch(() => null);
      const rentalEndMap = {};
      if (rentSnap?.exists()) {
        rentSnap.forEach(child => {
          const r = child.val();
          if (r.status !== 'cancelled' && r.carID && r.endDate) {
            rentalEndMap[r.carID] = r.endDate;
          }
        });
      }

      const list = [];
      snapshot.forEach((child) => {
        const car = { id: child.key, ...child.val() };

        // Hide car if its available date has passed
        if (isExpired(car.availableOn || car.availableFrom)) return;

        if (car.availability === 'Rented') {
          // Use rentalEndDate on car record first, fall back to rentalBookings
          const endDateStr = car.rentalEndDate || rentalEndMap[car.id];
          if (isExpired(endDateStr)) {
            // Only the car owner writes the availability reset — prevents every
            // connected client from racing to write the same update simultaneously.
            if (getAuth().currentUser?.uid === car.userID) {
              update(ref(db, `cars/${car.id}`), { availability: 'Available', rentalEndDate: null });
            }
            car.availability = 'Available';
          }
        }
        list.push(car);
      });
      list.sort((a, b) => (b.datePosted || '').localeCompare(a.datePosted || ''));
      setCars(list);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [db]);

  const filteredCars = cars.filter((car) => {
    if (filterAvailable && car.availability !== 'Available') return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (
        (car.make || '').toLowerCase().includes(q) ||
        (car.model || '').toLowerCase().includes(q) ||
        (car.location || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── Booking ──────────────────────────────────────────────────────────────

  const rentalDays = () => {
    if (!startDate || !endDate) return 0;
    const diff = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
    return Math.max(1, Math.round(diff));
  };

  const totalCost = () => {
    if (!selectedCar) return 0;
    return (parseInt(selectedCar.pricePerDay) || 0) * rentalDays();
  };

  const handleBook = async () => {
    if (!startDate || !endDate) { alert('Please select start and end dates.'); return; }
    if (new Date(endDate) <= new Date(startDate)) { alert('End date must be after start date.'); return; }
    const user = auth.currentUser;
    if (!user) return;

    setIsBooking(true);
    try {
      // Atomically check availability and flip to 'Rented' via a child-path transaction.
      // Operating on the availability child (not the full car node) means the renter only
      // needs write access to that specific field — compatible with the owner-only car rules
      // that allow any authed user to write availability and rentalEndDate children.
      const availRef = ref(db, `cars/${selectedCar.id}/availability`);
      const { committed } = await runTransaction(availRef, (availability) => {
        if (availability === null) return availability;  // null cache — Firebase will retry
        if (availability !== 'Available') return;        // abort — car already taken
        return 'Rented';
      });

      if (!committed) {
        alert('Sorry, this car is no longer available.');
        setIsBooking(false);
        return;
      }

      // Single multi-path update — rentalEndDate on the car and the rentalBookings
      // record are written atomically. If either fails, neither lands and the car
      // stays marked Rented without a dangling booking record.
      const fmt = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const rentalKey = push(ref(db, 'rentalBookings')).key;
      const updates = {
        [`cars/${selectedCar.id}/rentalEndDate`]: endDate,
        [`rentalBookings/${rentalKey}`]: {
          carID: selectedCar.id,
          ownerUserID: selectedCar.userID,
          renterUserID: user.uid,
          startDate: fmt(startDate),
          endDate: fmt(endDate),
          totalCost: totalCost(),
          pricePerDay: selectedCar.pricePerDay,
          rentalDays: rentalDays(),
          message: bookingMessage,
          status: 'active',
          timestamp: serverTimestamp(),
        },
      };
      try {
        await update(ref(db), updates);
      } catch (err) {
        await set(availRef, 'Available').catch(() => {}); // release the car again
        throw err;
      }
      // Link renter and owner so each can read the other's contact, then show it.
      await recordConnection('rentals', rentalKey, user.uid, selectedCar.userID);
      const owner = await fetchContact(selectedCar.userID);
      alert(`Car booked! ${rentalDays()} day(s) · $${totalCost()} total.` +
        (owner.number ? `\nContact ${owner.name || 'the owner'}: ${owner.number}` : ''));
      setSelectedCar(null);
      setStartDate(''); setEndDate(''); setBookingMessage('');
    } catch (err) {
      alert('Error booking car: ' + err.message);
    }
    setIsBooking(false);
  };

  // ── Post Car ─────────────────────────────────────────────────────────────

  const handlePostChange = (e) => {
    setPostForm({ ...postForm, [e.target.name]: e.target.value });
  };

  const handlePostCar = async (e) => {
    e.preventDefault();
    const { make, model, year, color, pricePerDay, location } = postForm;
    if (!make || !model || !year || !color || !pricePerDay || !location) {
      alert('Please fill in all required fields.'); return;
    }

    // Validation
    const yearNum = parseInt(year);
    const priceNum = parseInt(pricePerDay);
    const mileageNum = postForm.mileage ? parseInt(postForm.mileage) : null;

    if (isNaN(yearNum) || yearNum < 1990 || yearNum > 2026) {
      alert('Please enter a valid year between 1990 and 2026.');
      return;
    }
    if (isNaN(priceNum) || priceNum < 0 || priceNum > 500) {
      alert('Price per day must be between $0 and $500.');
      return;
    }
    if (mileageNum !== null && (isNaN(mileageNum) || mileageNum < 0 || mileageNum > 400000)) {
      alert('Please enter a valid mileage (0 – 400,000).');
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    setIsPosting(true);
    try {
      const userRef = ref(db, `users/${user.uid}`);
      // Only the display name is copied onto the listing; phone and email are
      // shared after a rental.
      let userName = '', userUsername = '';
      await new Promise((resolve) => {
        onValue(userRef, (snap) => {
          const d = snap.val() || {};
          userName = d.name || ''; userUsername = d.username || '';
          resolve();
        }, { onlyOnce: true });
      });

      const carData = {
        ...postForm,
        userID: user.uid,
        userName, userUsername,
        availability: 'Available',
        datePosted: new Date().toISOString(),
      };
      const newRef = push(ref(db, 'cars'));
      await set(newRef, carData);
      alert('Car posted successfully!');
      setShowPostForm(false);
      setPostForm({ make:'',model:'',year:'',color:'',licensePlate:'',pricePerDay:'',location:'',description:'',mileage:'',transmission:'Automatic',fuelType:'Gas',seats:'5',availableOn:'' });
    } catch (err) {
      alert('Error posting car: ' + err.message);
    }
    setIsPosting(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="borrow-container">
      {/* Header */}
      <div className="borrow-header">
        <h1>Borrow a Car</h1>
        <button className="post-car-btn" onClick={() => setShowPostForm(true)}>+ Post Your Car</button>
      </div>

      {/* Search + filter */}
      <div className="borrow-controls">
        <input
          className="borrow-search"
          type="text"
          placeholder="Search by make, model, or location…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <label className="borrow-filter-label">
          <input type="checkbox" checked={filterAvailable} onChange={(e) => setFilterAvailable(e.target.checked)} />
          Available only
        </label>
      </div>

      {/* Car list */}
      {isLoading ? (
        <p className="borrow-loading">Loading cars…</p>
      ) : filteredCars.length === 0 ? (
        <div className="borrow-empty">
          <p>No cars available</p>
          <p>Be the first to post a car!</p>
        </div>
      ) : (
        <div className="car-list">
          {filteredCars.map((car) => (
            <div key={car.id} className="car-card">
              <div className="car-card-top">
                <div>
                  <div className="car-title">{car.year} {car.make} {car.model}</div>
                  <div className="car-color">{car.color}</div>
                </div>
                <div>
                  <span className={`avail-badge ${car.availability === 'Available' ? 'avail-yes' : 'avail-no'}`}>
                    {car.availability}
                  </span>
                </div>
              </div>

              <div className="car-price">${car.pricePerDay}<span>/day</span></div>

              <div className="car-meta">
                {car.location && <span>{car.location}</span>}
                {car.transmission && <span>{car.transmission}</span>}
                {car.seats && <span>{car.seats} seats</span>}
                {car.fuelType && <span>{car.fuelType}</span>}
              </div>

              {car.description && <p className="car-desc">{car.description}</p>}

              <div className="car-owner">
                <span>{car.userName || 'Anonymous'}</span>
              </div>

              {car.availability === 'Available' && auth.currentUser?.uid !== car.userID && (
                <button
                  className="rent-btn"
                  onClick={() => { setSelectedCar(car); setStartDate(''); setEndDate(''); setBookingMessage(''); }}
                >
                  Book This Car
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Booking Modal ── */}
      {selectedCar && (
        <div className="modal-overlay" onClick={() => setSelectedCar(null)}>
          <div className="modal borrow-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Book Car</h2>
            <p className="modal-car-name">{selectedCar.year} {selectedCar.make} {selectedCar.model} · <strong>${selectedCar.pricePerDay}/day</strong></p>

            <label>Start Date
              <input type="date" value={startDate} min={new Date().toISOString().split('T')[0]}
                onChange={(e) => setStartDate(e.target.value)} />
            </label>

            <label>End Date
              <input type="date" value={endDate} min={startDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setEndDate(e.target.value)} />
            </label>

            {startDate && endDate && (
              <div className="cost-summary">
                <span>{rentalDays()} day{rentalDays() !== 1 ? 's' : ''}</span>
                <span className="total-cost">Total: <strong>${totalCost()}</strong></span>
              </div>
            )}

            <label>Message to owner (optional)
              <textarea value={bookingMessage} onChange={(e) => setBookingMessage(e.target.value)}
                placeholder="Any details for the owner…" rows={3} maxLength={300} />
            </label>

            <div className="modal-actions">
              <button className="confirm-btn" onClick={handleBook} disabled={isBooking}>
                {isBooking ? 'Booking…' : 'Confirm Booking'}
              </button>
              <button className="cancel-btn" onClick={() => setSelectedCar(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Post Car Modal ── */}
      {showPostForm && (
        <div className="modal-overlay" onClick={() => setShowPostForm(false)}>
          <div className="modal post-car-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Post Your Car</h2>
            <form onSubmit={handlePostCar}>

              <div className="form-row">
                <label>Make *
                  <select name="make" value={postForm.make} onChange={handlePostChange} required>
                    <option value="">Select make</option>
                    {CAR_MAKES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label>Model *
                  <input name="model" value={postForm.model} onChange={handlePostChange} placeholder="e.g. Camry" maxLength={50} required />
                </label>
              </div>

              <div className="form-row">
                <label>Year *
                  <input type="number" name="year" value={postForm.year} onChange={handlePostChange} placeholder="e.g. 2022" min="1990" max="2026" required />
                </label>
                <label>Color *
                  <input name="color" value={postForm.color} onChange={handlePostChange} placeholder="e.g. Silver" maxLength={30} required />
                </label>
              </div>

              <div className="form-row">
                <label>Price per Day ($) *
                  <input type="number" name="pricePerDay" value={postForm.pricePerDay} onChange={handlePostChange} placeholder="e.g. 40" required min="0" max="500" />
                </label>
                <label>Location *
                  <input name="location" value={postForm.location} onChange={handlePostChange} placeholder="e.g. USAFA Campus" maxLength={100} required />
                </label>
              </div>

              <div className="form-row">
                <label>Transmission
                  <select name="transmission" value={postForm.transmission} onChange={handlePostChange}>
                    {TRANSMISSION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label>Fuel Type
                  <select name="fuelType" value={postForm.fuelType} onChange={handlePostChange}>
                    {FUEL_TYPES.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </label>
              </div>

              <div className="form-row">
                <label>Seats
                  <select name="seats" value={postForm.seats} onChange={handlePostChange}>
                    {SEAT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>Mileage (optional)
                  <input type="number" name="mileage" value={postForm.mileage} onChange={handlePostChange} placeholder="e.g. 25000" min="0" max="400000" />
                </label>
              </div>

              <div className="form-row">
                <label>Available On
                  <input type="date" name="availableOn" value={postForm.availableOn} onChange={handlePostChange} />
                </label>
              </div>

              <label>License Plate
                <input name="licensePlate" value={postForm.licensePlate} onChange={handlePostChange} placeholder="e.g. ABC-1234" />
              </label>

              <label>Description (optional)
                <textarea name="description" value={postForm.description} onChange={handlePostChange}
                  placeholder="Tell renters about your car…" rows={3} maxLength={300} />
              </label>

              <div className="modal-actions">
                <button type="submit" className="confirm-btn" disabled={isPosting}>
                  {isPosting ? 'Posting…' : 'Post Car'}
                </button>
                <button type="button" className="cancel-btn" onClick={() => setShowPostForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Borrow;
